import { Injectable, computed, inject, signal } from '@angular/core';
import { DocumentosApiService, claveErrorDocumentos } from './documentos-api.service';
import type { DocumentArtifact, DocumentoFormato } from './documento.types';

export type EstadoExportacion = 'generando' | 'listo' | 'error';

/** Una exportación pedida: la bandeja la pinta desde que se pide hasta que se cierra. */
export interface Exportacion {
  /** `threadId:formato` (conversación) o `threadId:r<huella>:formato` (una respuesta): como
   *  mucho una por hilo, ámbito y formato. */
  clave: string;
  threadId: string;
  /** Principio de la respuesta exportada; ausente = la conversación entera. */
  texto?: string;
  /** Título de la conversación tal como lo ve el usuario (puede venir vacío). */
  titulo: string;
  formato: DocumentoFormato;
  estado: EstadoExportacion;
  /** `Date.now()` al pedirla: la bandeja cuenta los segundos desde aquí. */
  inicio: number;
  doc?: DocumentArtifact;
  errorKey?: string;
}

/** Exportaciones terminadas que se conservan a la vista; las en curso no cuentan. */
const MAX_TERMINADAS = 3;

/** Cuánto de la respuesta se manda al servidor para localizarla en el hilo. */
const MAX_TEXTO_RESPUESTA = 300;

/** Huella corta y estable del texto (djb2): distingue las respuestas de un mismo hilo. */
function huella(texto: string): string {
  let h = 5381;
  for (let i = 0; i < texto.length; i++) h = ((h << 5) + h + texto.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/** Principio de una respuesta tal como se manda y se identifica. */
const inicioDe = (texto: string) => texto.trim().slice(0, MAX_TEXTO_RESPUESTA);

function claveDe(threadId: string, formato: DocumentoFormato, texto?: string): string {
  const inicio = texto ? inicioDe(texto) : '';
  return inicio ? `${threadId}:r${huella(inicio)}:${formato}` : `${threadId}:${formato}`;
}

const mismosFormatos = (a: readonly DocumentoFormato[], b: readonly DocumentoFormato[]) =>
  a.length === b.length && a.every((f, i) => f === b[i]);

/**
 * «Exportar conversación» (SYN-930): estado compartido entre los sitios desde los que se
 * pide —fila del listado lateral, barra bajo la última respuesta, página de historial— y
 * la bandeja global que enseña el progreso y el resultado.
 *
 * Vive aquí y no en cada botón porque la exportación sobrevive a quien la pidió: el
 * servidor tarda varios segundos en maquetar, y entretanto el usuario puede navegar,
 * cerrar el diálogo del listado o abrir otra conversación. El resultado ya no se cuelga
 * del chat (antes aparecía al final, fuera de la vista y sin aviso de que se estaba
 * generando): se queda en la bandeja con «Vista previa» y «Descargar».
 */
@Injectable({ providedIn: 'root' })
export class ExportarConversacionService {
  private readonly api = inject(DocumentosApiService);

  private readonly formatosSignal = signal<DocumentoFormato[]>([], { equal: mismosFormatos });
  /** Formatos que admite el servidor; vacío = función apagada (no se ofrece el botón). */
  readonly formatos = this.formatosSignal.asReadonly();
  private pidiendoFormatos = false;

  private readonly lista = signal<Exportacion[]>([]);
  /** Más reciente primero. */
  readonly exportaciones = this.lista.asReadonly();

  private readonly enCurso = computed(() => new Set(
    this.lista().filter(e => e.estado === 'generando').map(e => e.clave),
  ));

  /** Documento abierto en el visor de la bandeja. */
  readonly visor = signal<DocumentArtifact | null>(null);

  private readonly cancelaciones = new Map<string, AbortController>();

  /**
   * Pregunta al servidor qué formatos admite. La respuesta la cachea el servicio de la
   * API (y reintenta pasados unos segundos si falló), así que se puede llamar desde cada
   * sitio que pinte el botón sin multiplicar peticiones.
   */
  cargarFormatos(): void {
    if (this.pidiendoFormatos) return;
    this.pidiendoFormatos = true;
    void this.api.capacidades()
      .then(c => this.formatosSignal.set(c.enabled ? c.exportarFormatos : []))
      .finally(() => { this.pidiendoFormatos = false; });
  }

  /** ¿Se está generando alguna exportación de este hilo (en cualquier formato)? Con `texto`,
   *  solo las de esa respuesta; sin él, solo las de la conversación entera. */
  exportando(threadId: string, texto?: string): boolean {
    const inicio = texto ? inicioDe(texto) : '';
    const prefijo = inicio ? `${threadId}:r${huella(inicio)}:` : `${threadId}:`;
    // Tras el prefijo solo queda el formato; si queda más, es de otro ámbito (una respuesta).
    for (const clave of this.enCurso()) {
      if (clave.startsWith(prefijo) && !clave.slice(prefijo.length).includes(':')) return true;
    }
    return false;
  }

  /** ¿Se está generando este hilo (o esta respuesta, con `texto`) en este formato? */
  generando(threadId: string, formato: DocumentoFormato, texto?: string): boolean {
    return this.enCurso().has(claveDe(threadId, formato, texto));
  }

  /** Exporta la conversación entera o, con `texto`, solo esa respuesta del agente. */
  async exportar(threadId: string, titulo: string, formato: DocumentoFormato, texto?: string): Promise<void> {
    const clave = claveDe(threadId, formato, texto);
    if (this.enCurso().has(clave)) return;

    const inicio = texto ? inicioDe(texto) : '';
    const nueva: Exportacion = {
      clave, threadId, titulo, formato, estado: 'generando', inicio: Date.now(),
      ...(inicio ? { texto: inicio } : {}),
    };
    this.lista.update(l => recortar([nueva, ...l.filter(e => e.clave !== clave)]));
    const control = new AbortController();
    this.cancelaciones.set(clave, control);

    try {
      const doc = inicio
        ? await this.api.exportarRespuesta(threadId, formato, inicio, control.signal)
        : await this.api.exportarHilo(threadId, formato, control.signal);
      this.actualizar(clave, nueva.inicio, { estado: 'listo', doc });
    } catch (err) {
      if (control.signal.aborted) return;
      this.actualizar(clave, nueva.inicio, { estado: 'error', errorKey: claveErrorDocumentos(err) });
    } finally {
      if (this.cancelaciones.get(clave) === control) this.cancelaciones.delete(clave);
    }
  }

  reintentar(e: Exportacion): void {
    void this.exportar(e.threadId, e.titulo, e.formato, e.texto);
  }

  /**
   * Quita una exportación de la bandeja. Si aún se estaba generando, deja de esperarla:
   * el servidor puede terminarla igual, pero ese documento caduca solo (TTL del servidor).
   */
  descartar(clave: string): void {
    this.cancelaciones.get(clave)?.abort();
    this.cancelaciones.delete(clave);
    this.lista.update(l => l.filter(e => e.clave !== clave));
  }

  /** Aplica el resultado solo si la exportación sigue siendo la misma que se pidió
   *  (no se descartó ni se volvió a pedir entretanto). */
  private actualizar(clave: string, inicio: number, cambios: Partial<Exportacion>): void {
    this.lista.update(l => recortar(l.map(e => (e.clave === clave && e.inicio === inicio ? { ...e, ...cambios } : e))));
  }
}

/** Conserva todas las exportaciones en curso y solo las `MAX_TERMINADAS` terminadas más recientes. */
function recortar(lista: Exportacion[]): Exportacion[] {
  let terminadas = 0;
  return lista.filter(e => e.estado === 'generando' || ++terminadas <= MAX_TERMINADAS);
}
